/**
 * 매매검색 상세검색 조건 (CS, 2026-09-23).
 *
 * 매매검색(TotalSearch)과 매수고객 카드(UserDetail)의 매물 검색이 **같은 조건·같은 요청**을 쓰게
 * 하려고 TotalSearch 안에 있던 조립 코드를 그대로 옮겼다. 사용자 요청이 "기존 매매검색의 상세검색
 * 기능을 동일하게" 였으므로 한쪽만 고치면 두 화면이 조용히 어긋난다. 상세검색 팝업 화면은
 * components/SellDetailSearchPopup.vue 다.
 *
 * 요청 문자열은 예전 TotalSearch 가 만들던 것과 **바이트 단위로 같다**(sellSearch.test.mjs 가
 * 옛 코드를 그대로 두고 대조한다). 값의 인코딩도 예전처럼 하지 않는다.
 */

export const SELL_STATUS_ALL = ['sell', 'ready', 'hold', 'done'];

export function defaultSellSearchOptions() {
	return {
		keyword: '',
		st_: ['done'],
		st: false,
		bName: '', //물건명
		oName: '', //소유자명
		useList: [], //용도지역

		lp_S: '', //대지평수
		lp_E: '',
		lm_S: '', //대지면적
		lm_E: '',

		er_S: '', //연수익률
		er_E: '',
		sub: '', //주변역
		dis: '', //거리
		floor_S: '', //층수
		floor_E: '',
		stList: [], //상태
		bId: '', //물건번호
		preOName: '', //전소유자명
		sellPrice_S: '', //매물금액
		sellPrice_E: '',

		sizeP_S: '', //건물평수
		sizeP_E: '',
		sizeM_S: '', //건물면적
		sizeM_E: '',

		roadWide_S: '', //도로너비
		roadWide_E: '',
		roadConer: '', //코너
		roadDual: '', //양면
		roadName: '', //도로명
		memUid: '', //담당자
		regDate_S: '', //매물등록일자
		regDate_E: '',
		phone: '', //전화번호
		cateList: [], //매물종류
		sellDate_S: '', //매매일자
		sellDate_E: '',
		remodelDate_S: '', //리모델링일
		remodelDate_E: '',
		buildDate_S: '', //준공일자
		buildDate_E: '',
		landSizePy: '', //토지평단가
		totalSizePy: '', //연면적평단가
		teamId: '', //부서/팀
		cateId: '', //팀
		userId: '',
	};
}

/** '최근 1년 등록만' 창: 매물등록일자(done_date_time) 기준 [오늘-1년, 오늘]. */
export function recentYearWindow(now = new Date()) {
	const pad = n => String(n).padStart(2, '0');
	const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
	const eDate = new Date(now.getTime());
	const sDate = new Date(now.getTime());
	sDate.setFullYear(sDate.getFullYear() - 1);
	return { s: fmt(sDate), e: fmt(eDate) };
}

/**
 * 입력 상태(searchOptions_) → 검색에 쓰는 값(searchOptions).
 * 용도지역·매물종류는 선택한 객체에서 id 만 뽑고, 범위의 한쪽만 적었으면 반대쪽을 끝값으로 채운다.
 * 비교는 예전 코드처럼 느슨한 같음(==)이다. 숫자 입력칸의 0 도 '비어 있음' 으로 본다.
 */
export function normalizeSellSearch(src) {
	const o = JSON.parse(JSON.stringify(src));
	// F-1.2: trim whitespace-only keyword to prevent match-all
	if (o.keyword && o.keyword.trim() === '') {
		o.keyword = '';
	}

	o.useList = [];
	for (let i = 0; i < src.useList.length; i++) {
		o.useList.push(src.useList[i].id);
	}

	o.cateList = [];
	for (let i = 0; i < src.cateList.length; i++) {
		o.cateList.push(src.cateList[i].id);
	}

	const fill = (s, e, lo, hi) => {
		if (o[s] == '' && o[e] != '') {
			o[s] = lo;
		} else if (o[s] != '' && o[e] == '') {
			o[e] = hi;
		}
	};
	fill('lp_S', 'lp_E', 0, 99999999); // 대지평수
	fill('er_S', 'er_E', 0, 999); // 연수익률
	fill('floor_S', 'floor_E', 0, 999); // 층수
	fill('sellPrice_S', 'sellPrice_E', 0, 99999999); // 매물금액
	fill('sizeP_S', 'sizeP_E', 0, 99999999); // 건물평수
	fill('roadWide_S', 'roadWide_E', 0, 32767); // 도로너비
	fill('regDate_S', 'regDate_E', '1900-01-01', '2999-01-01'); // 매물등록일자
	fill('sellDate_S', 'sellDate_E', '1900-01-01', '2999-01-01'); // 매매일자
	fill('remodelDate_S', 'remodelDate_E', '1900-01-01', '2999-01-01'); // 리모델링일
	fill('buildDate_S', 'buildDate_E', '1900-01-01', '2999-01-01'); // 준공일자
	return o;
}

// 요청에 싣는 순서. /total · /total/count · /sms 가 같은 순서로 받는다.
const PARAM_KEYS = [
	'st_', 'keyword', 'bName', 'oName', 'useList', 'lp_S', 'lp_E', 'er_S', 'er_E', 'sub', 'dis',
	'floor_S', 'floor_E', 'stList', 'bId', 'preOName', 'sellPrice_S', 'sellPrice_E', 'sizeP_S', 'sizeP_E',
	'roadWide_S', 'roadWide_E', 'roadConer', 'roadDual', 'roadName', 'memUid', 'regDate_S', 'regDate_E',
	'phone', 'cateList', 'sellDate_S', 'sellDate_E', 'remodelDate_S', 'remodelDate_E', 'buildDate_S',
	'buildDate_E', 'landSizePy', 'totalSizePy', 'teamId', 'userId',
];

/** '&st_=...&userId=...' — 앞에 'page=N' 을 붙여 /total·/total/count 에, 그대로 /sms 에 쓴다. */
export function sellSearchParams(o) {
	let q = '';
	for (const k of PARAM_KEYS) q += '&' + k + '=' + o[k];
	return q;
}

// 적용 중인 조건을 표 위 태그로 보여 줄 때 쓰는 이름·단위. 매물상태(st_)는 체크박스가 이미 보여 준다.
const RANGE_TAGS = [
	['sellPrice', '매물금액', '억'],
	['lp', '대지평수', '평'],
	['sizeP', '건물평수', '평'],
	['er', '연수익률', '%'],
	['floor', '층수', '층'],
	['roadWide', '도로너비', 'm'],
	['sellDate', '매매일자', ''],
	['remodelDate', '리모델링일', ''],
	['buildDate', '준공일자', ''],
];
const TEXT_TAGS = [
	['bName', '물건명'],
	['bId', '물건번호'],
	['oName', '소유자'],
	['preOName', '전소유자'],
	['phone', '전화번호'],
	['roadName', '도로명'],
	['sub', '주변역'],
	['landSizePy', '토지평단가'],
	['totalSizePy', '연면적평단가'],
];

/**
 * 검색에 쓴 입력값(정규화 전) → ['매물금액 10~300억', '용도지역 일반상업 외 1', …].
 * 비어 있음의 판정은 요청 쪽(normalizeSellSearch)과 같은 느슨한 비교다. 그래서 태그가 보이면 그 조건이
 * 실제로 요청에 걸려 있다. '최근 1년 등록만' 을 켠 등록일 창은 체크박스가 보여 주므로 뺀다.
 */
export function sellFilterTags(raw, { recentYear = false } = {}) {
	const empty = v => v == null || v == '';
	const tags = [];
	for (const [k, label] of TEXT_TAGS) {
		if (!empty(raw[k]) && String(raw[k]).trim() !== '') tags.push(`${label} ${String(raw[k]).trim()}`);
	}
	for (const [k, label] of [['cateList', '매물종류'], ['useList', '용도지역']]) {
		const list = raw[k] || [];
		if (list.length) tags.push(`${label} ${list[0].name}${list.length > 1 ? ` 외 ${list.length - 1}` : ''}`);
	}
	if ((raw.stList || []).length) tags.push(`상태 ${raw.stList.join(',')}`);
	const ranges = recentYear ? RANGE_TAGS : [...RANGE_TAGS, ['regDate', '매물등록일자', '']];
	for (const [k, label, unit] of ranges) {
		const s = raw[k + '_S'];
		const e = raw[k + '_E'];
		if (!empty(s) && !empty(e)) tags.push(`${label} ${s}~${e}${unit}`);
		else if (!empty(s)) tags.push(`${label} ${s}${unit} 이상`);
		else if (!empty(e)) tags.push(`${label} ${e}${unit} 이하`);
	}
	if (raw.roadConer === 'Y') tags.push('코너');
	if (raw.roadDual === 'Y') tags.push('양면');
	if (!empty(raw.memUid)) tags.push('담당자 지정');
	if (!empty(raw.teamId)) tags.push('팀 지정');
	return tags;
}
